import { DesktopOutlined, MoonOutlined, SunOutlined } from '@ant-design/icons';
import { Radio } from 'antd';
import type { RadioChangeEvent } from 'antd';
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks';
import type { KeyboardEvent, ReactNode } from 'react';
import { setThemePreference } from 'store/slices/themeSlice';
import type { ThemePreference } from 'types';
import 'styles/sass/theme-selector.scss';

const themeOptions: Array<{
  icon: ReactNode;
  label: string;
  value: ThemePreference;
}> = [
  {
    icon: <DesktopOutlined aria-hidden="true" />,
    label: 'Sistema',
    value: 'system',
  },
  {
    icon: <SunOutlined aria-hidden="true" />,
    label: 'Claro',
    value: 'light',
  },
  {
    icon: <MoonOutlined aria-hidden="true" />,
    label: 'Oscuro',
    value: 'dark',
  },
];

interface ThemeSelectorProps {
  className?: string;
}

export const ThemeSelector = ({ className = '' }: ThemeSelectorProps) => {
  const dispatch = useAppDispatch();
  const preference = useAppSelector((state) => state.theme.preference);

  const handlePreferenceChange = (event: RadioChangeEvent) => {
    dispatch(setThemePreference(event.target.value as ThemePreference));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const optionValues = themeOptions.map(({ value }) => value);
    const currentIndex = optionValues.indexOf(preference);
    let nextIndex: number | undefined;

    if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = optionValues.length - 1;
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % optionValues.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex =
        (currentIndex - 1 + optionValues.length) % optionValues.length;
    }

    if (nextIndex === undefined) {
      return;
    }

    event.preventDefault();
    const nextPreference = optionValues[nextIndex];
    dispatch(setThemePreference(nextPreference));
    event.currentTarget
      .querySelector<HTMLInputElement>(
        `[data-cy="theme-option-${nextPreference}"]`
      )
      ?.focus();
  };

  return (
    <div className="theme-selector-keyboard-handler" onKeyDown={handleKeyDown}>
      <Radio.Group
        aria-label="Seleccionar apariencia"
        buttonStyle="solid"
        className={`theme-selector ${className}`.trim()}
        data-cy="theme-selector"
        name="theme-preference"
        onChange={handlePreferenceChange}
        value={preference}
      >
        {themeOptions.map(({ icon, label, value }) => (
          <Radio.Button
            aria-label={`Apariencia: ${label}`}
            data-cy={`theme-option-${value}`}
            key={value}
            title={label}
            value={value}
          >
            {icon}
            <span className="theme-selector-label">{label}</span>
          </Radio.Button>
        ))}
      </Radio.Group>
    </div>
  );
};
